import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import Spinner from '@/components/ui/Spinner'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProtectedRouteProps {
  /**
   * When true the route additionally requires the authenticated user to have
   * the ROLE_ADMIN role. Users without it are redirected to /app/dashboard.
   */
  requireAdmin?: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * ProtectedRoute
 *
 * Guards a React Router v6 layout route (or any route that renders <Outlet />).
 *
 * Behaviour:
 *   1. While the auth context is still hydrating (isLoading === true) we show
 *      a full-screen spinner so the user never sees a flash-redirect.
 *   2. If the user is not authenticated we redirect them to /login, preserving
 *      the original `location` in `state.from` so the login page can send them
 *      back after a successful sign-in.
 *   3. If `requireAdmin` is set and the user lacks ROLE_ADMIN we redirect them
 *      to /app/dashboard — they are authenticated but not authorised.
 *   4. Otherwise the child routes are rendered via <Outlet />.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requireAdmin = false,
}) => {
  const { isLoading, isAuthenticated, user } = useAuth()
  const location = useLocation()

  // ── 1. Auth still loading — show a centred spinner ────────────────────────
  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background"
        aria-busy="true"
        aria-label="Checking authentication…"
      >
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" className="text-primary" />
          <p className="text-sm text-muted-foreground font-medium select-none">
            Loading…
          </p>
        </div>
      </div>
    )
  }

  // ── 2. Not authenticated — redirect to login ──────────────────────────────
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    )
  }

  // ── 3. Admin-only route but user lacks the role ───────────────────────────
  if (requireAdmin) {
    const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false

    if (!isAdmin) {
      return (
        <Navigate
          to="/app/dashboard"
          replace
        />
      )
    }
  }

  // ── 4. All checks passed — render child routes ────────────────────────────
  return <Outlet />
}

export default ProtectedRoute
